/**
 * PSV-1904 — in-arena chat overlay.
 *
 * INPUT ONLY — the message feed renders in-canvas (ChatFeed.ts): DOM
 * ignores the Phaser FIT scale and always paints above the canvas, so a
 * DOM feed collided with panels and even modals. The input stays DOM
 * (text entry in Phaser is painful) and is intentionally modal while
 * open: centered near the bottom, Enter sends, Esc/blur closes.
 *
 * While the input is open, a capture-phase keydown listener stops
 * propagation of everything except Escape so WASD/fire/hotkeys never
 * leak into the scene (EntryOverlay pattern).
 */

const MAX_LEN = 120;

const STYLE = `
  .sb-chat-root {
    position: fixed;
    left: 50%;
    bottom: 12vh;
    transform: translateX(-50%);
    width: min(440px, 80vw);
    z-index: 9000;
    font-family: ui-monospace, Menlo, Consolas, monospace;
    pointer-events: none;
  }
  .sb-chat-inputrow {
    display: none;
    pointer-events: auto;
  }
  .sb-chat-inputrow--open { display: block; }
  .sb-chat-input {
    width: 100%;
    box-sizing: border-box;
    padding: 6px 8px;
    font-family: inherit;
    font-size: 13px;
    color: #eaf6ff;
    background: #0b101c;
    border: 1px solid #2c3a5c;
    border-radius: 6px;
    outline: none;
  }
  .sb-chat-input:focus { border-color: #35c4f0; }
`;

export class ChatOverlay {
  private readonly onSend: (text: string) => void;
  private root: HTMLDivElement | null = null;
  private styleEl: HTMLStyleElement | null = null;
  private inputRow: HTMLDivElement | null = null;
  private input: HTMLInputElement | null = null;
  private open = false;
  private destroyed = false;

  private readonly onKeydownCapture = (event: KeyboardEvent): void => {
    if (!this.open || this.destroyed) return;
    if (event.key === 'Escape') {
      this.close();
      return;
    }
    event.stopPropagation();
    if (event.key === 'Enter') this.submit();
  };

  constructor(onSend: (text: string) => void) {
    this.onSend = onSend;
    this.build();
    document.addEventListener('keydown', this.onKeydownCapture, true);
  }

  get isOpen(): boolean {
    return this.open;
  }


  /** Enter pressed in-game: reveal the input and focus it. */
  openInput(): void {
    if (this.destroyed || this.open) return;
    this.open = true;
    this.inputRow?.classList.add('sb-chat-inputrow--open');
    this.input?.focus();
  }

  close(): void {
    this.open = false;
    if (this.input !== null) this.input.value = '';
    this.inputRow?.classList.remove('sb-chat-inputrow--open');
    this.input?.blur();
  }


  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    document.removeEventListener('keydown', this.onKeydownCapture, true);
    this.root?.remove();
    this.styleEl?.remove();
    this.root = null;
    this.styleEl = null;
    this.inputRow = null;
    this.input = null;
  }

  // ---------- private ----------

  private submit(): void {
    const raw = this.input?.value ?? '';
    const text = raw.trim().replace(/\s+/g, ' ').slice(0, MAX_LEN);
    this.close();
    if (text.length === 0) return;
    this.onSend(text);
  }

  private build(): void {
    this.styleEl = document.createElement('style');
    this.styleEl.textContent = STYLE;
    document.head.appendChild(this.styleEl);

    this.root = document.createElement('div');
    this.root.className = 'sb-chat-root';

    this.inputRow = document.createElement('div');
    this.inputRow.className = 'sb-chat-inputrow';
    this.input = document.createElement('input');
    this.input.className = 'sb-chat-input';
    this.input.maxLength = MAX_LEN;
    this.input.placeholder = 'Say something… (Enter — send, Esc — close)';
    this.input.addEventListener('blur', () => {
      // Blur = close, but let a click on the input itself not re-trigger.
      window.setTimeout(() => {
        if (this.open) this.close();
      }, 50);
    });
    this.inputRow.appendChild(this.input);
    this.root.append(this.inputRow);
    document.body.appendChild(this.root);
  }
}
